import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { AccountsService } from '../../accounts.service';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { SessionStorageService } from '../../../services/session-storage.service';
import { ApMatchingDashboard, BillMatchEvaluation } from '../phase7-ap-controls.model';

@Component({
    selector: 'app-ap-matching-dashboard',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterModule, ButtonModule, InputTextModule, TableModule, TagModule, ToastModule, PageShellComponent, PageHeroComponent, PageMetricStripComponent, PageDataPanelComponent],
    providers: [MessageService],
    templateUrl: './matching-dashboard.component.html',
    styleUrls: ['./matching-dashboard.component.css']
})
export class MatchingDashboardComponent implements OnInit {
    dashboard: ApMatchingDashboard = {};
    loading = false;
    companyId = 0;
    sourceReferenceNumber = '';

    constructor(
        private accountsService: AccountsService,
        private sessionStorageService: SessionStorageService,
        private router: Router,
        private messageService: MessageService
    ) {}

    ngOnInit(): void {
        this.companyId = this.sessionStorageService.getCompany()?.id ?? 0;
        this.load();
    }

    get metrics(): PageMetric[] {
        return [
            { label: 'Matched', value: this.dashboard.matchedCount ?? 0, hint: this.money(this.dashboard.matchedAmount), tone: 'success' },
            { label: 'Pending', value: this.dashboard.pendingCount ?? 0, hint: this.money(this.dashboard.pendingAmount), tone: 'info' },
            { label: 'Exceptions', value: this.dashboard.exceptionCount ?? 0, hint: this.money(this.dashboard.exceptionAmount), tone: 'danger' },
            { label: 'Overdue Exceptions', value: this.dashboard.overdueExceptionCount ?? 0, hint: this.money(this.dashboard.overdueExceptionAmount), tone: 'warn' }
        ];
    }

    get exceptions(): BillMatchEvaluation[] {
        return this.dashboard.exceptions ?? [];
    }

    load(): void {
        if (!this.companyId) return;
        this.loading = true;
        this.accountsService.getApMatchingDashboard(this.companyId).subscribe({
            next: dashboard => {
                this.dashboard = dashboard ?? {};
                this.loading = false;
            },
            error: err => {
                this.loading = false;
                this.messageService.add({ severity: 'error', summary: 'Matching dashboard failed', detail: err.error?.message || 'Could not load AP matching dashboard.' });
            }
        });
    }

    evaluate(row: BillMatchEvaluation): void {
        if (!row.billId) return;
        this.accountsService.evaluateBillMatch(row.billId, {
            actorId: this.actorId(),
            sourceReferenceNumber: this.sourceReferenceNumber || row.sourceReferenceNumber
        }).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Evaluated', detail: `${row.billNumber || 'Bill'} was evaluated.` });
                this.load();
            },
            error: err => this.messageService.add({ severity: 'error', summary: 'Evaluation failed', detail: err.error?.message || 'Could not evaluate the bill.' })
        });
    }

    openBill(row: BillMatchEvaluation): void {
        if (row.billId) {
            this.router.navigate(['/accounts/enterprise/payables/generated-bills/view', row.billId]);
        }
    }

    statusSeverity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        if (status === 'MATCHED' || status === 'OVERRIDE_APPROVED') return 'success';
        if (status === 'MATCH_EXCEPTION') return 'danger';
        if (status === 'PENDING_RECEIPT') return 'warn';
        if (status === 'PENDING_MATCH') return 'info';
        return 'secondary';
    }

    money(amount?: number, currency = 'SAR'): string {
        return `${currency} ${Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
    }

    private actorId(): number | undefined {
        const user = this.sessionStorageService.getUser();
        return user?.id ?? user?.userId ?? this.sessionStorageService.getCustomer()?.id;
    }
}
